import { useParams, Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { NAV_GROUPS, hasPermission } from '@/config/navigationConfig';
import { hubSlug } from '@/components/NavGroup';
import { useAuthStore } from '@/stores';
import { usePendingOffsets } from '@/hooks/usePendingOffsets';
import { ArrowLeft, Folder, ChevronRight } from 'lucide-react';

/**
 * iter943 — Department Hub. A clean, big-tile landing for one top-level
 * sidebar department (Finance, Operations, HR, Settings, …). Reuses the
 * EXISTING routes from NAV_GROUPS — every tile just links to a page that
 * already exists. Permission-filtered with the same gate the sidebar uses.
 */
export default function HubPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const cu = user || {};
  const userRole = cu.role;
  const userPerms = cu.permissions || [];
  const allowedModules = cu.allowed_modules || [];
  const isSuperAdmin = !!cu.is_super_admin;
  const isAdmin = userRole === 'admin';

  const group = NAV_GROUPS.find((g) => hubSlug(g.label) === slug);
  const { count: pendingOffsets } = usePendingOffsets();

  const canSee = (item) => {
    if (item.superAdmin && !isSuperAdmin) return false;
    if (isAdmin) return true;
    if (item.roles && !item.roles.includes(userRole || 'operator')) return false;
    if (item.perm) return hasPermission(userPerms, item.perm, userRole, allowedModules);
    return true;
  };

  if (!group) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto p-8 text-center">
          <h1 className="text-xl font-bold mb-2">Hub not found</h1>
          <p className="text-muted-foreground text-sm mb-4">This department doesn't exist.</p>
          <button onClick={() => navigate('/')} className="text-primary font-medium hover:underline">Go to Dashboard</button>
        </div>
      </DashboardLayout>
    );
  }

  const topItems = (group.items || []).filter(canSee);
  const subs = (group.subgroups || [])
    .filter((s) => !s.hidden)  // iter — honor `hidden:true` subgroups (same as sidebar NavGroup)
    .map((s) => ({ label: s.label, items: (s.items || []).filter(canSee) }))
    .filter((s) => s.items.length > 0);

  const Tile = ({ item }) => {
    const Icon = item.icon || Folder;
    const showOffsets = item.path === '/reconciliation-center' && pendingOffsets > 0;
    return (
      <Link
        to={item.path}
        data-testid={`hub-tile-${item.path.replace(/\//g, '-')}`}
        className="group flex items-center gap-3.5 p-4 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-200"
      >
        <span className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
          <Icon size={22} className="text-primary" strokeWidth={1.9} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-sm text-foreground truncate">{item.label}</span>
        </span>
        {showOffsets && (
          <span
            className="shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
            data-testid="hub-pending-offsets-badge"
            title={`${pendingOffsets} pending cross-branch approvals`}
          >
            {pendingOffsets}
          </span>
        )}
        <ChevronRight size={16} className="text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
      </Link>
    );
  };

  const Grid = ({ items }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((it) => <Tile key={it.path} item={it} />)}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          data-testid="hub-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="text-3xl sm:text-4xl font-bold font-outfit mb-1" data-testid="hub-title">{group.label}</h1>
        <p className="text-muted-foreground text-sm mb-8">Choose where you want to go.</p>

        {topItems.length > 0 && (
          <div className="mb-8">
            <Grid items={topItems} />
          </div>
        )}

        {subs.map((s) => (
          <section key={s.label} className="mb-8" data-testid={`hub-section-${hubSlug(s.label)}`}>
            <h2 className="text-xs font-bold uppercase tracking-[0.08em] text-muted-foreground mb-3">{s.label}</h2>
            <Grid items={s.items} />
          </section>
        ))}

        {topItems.length === 0 && subs.length === 0 && (
          <p className="text-muted-foreground text-sm">You don't have access to any pages in this department.</p>
        )}
      </div>
    </DashboardLayout>
  );
}
